import React, { useState, useEffect } from 'react';
import { Event } from '../types/Event';
import { EventForm } from './EventForm.tsx';
import './CsvImport.css';
import { GeoPoint, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { eventService } from '../services/eventService.ts';
import { MissingFields } from './EventForm';

interface CsvImportProps {
  onClose: () => void;
}

interface ParseError {
  row: number;
  message: string;
}

interface TempEvent extends Partial<Event> {
  _tempLat?: number;
  _tempLng?: number;
  _tempImageUrl?: string;
  _missingFields?: MissingFields;
}

export const CsvImport: React.FC<CsvImportProps> = ({ onClose }) => {
  const [events, setEvents] = useState<TempEvent[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [parseErrors, setParseErrors] = useState<ParseError[]>([]);
  const [imageLoadingStatus, setImageLoadingStatus] = useState<{[key: string]: string}>({});
  const [missingFields, setMissingFields] = useState<MissingFields>({});
  
  const currentEvent = events[currentIndex];

  useEffect(() => {
    if (currentEvent) {
      console.log('Current event changed:', currentEvent);
    }
  }, [currentEvent]);

  useEffect(() => {
    console.log('Current index:', currentIndex);
    console.log('Current event:', events[currentIndex]);
  }, [currentIndex, events]);

  const parseCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const errors: ParseError[] = [];

      // Map headers to our expected format
      const headerMap: { [key: string]: string } = {
        'name': 'title',
        'Name': 'title',
        'Description': 'description',
        'description': 'description',
        'Location': 'place',
        'location': 'place',
        'location_name': 'place',
        'Start Date': 'startDateTime',
        'End Date': 'endDateTime',
        'Lat': 'latitude',
        'lat': 'latitude',
        'Lng': 'longitude',
        'lng': 'longitude',
        'imageUrl': 'imageUrl',
        'ImageUrl': 'imageUrl'
      };

      // Split by newlines but respect quotes
      const rows = text.match(/("[\s\S]*?"|[^"\n\r]+)(?=\s*[\n\r]+|\s*$)/g)
        ?.map(row => row.trim())
        .filter(row => row);

      if (!rows?.length) {
        console.error('No valid rows found');
        return;
      }

      // Get headers and clean them
      const headers = rows[0].split(',')
        .map(header => header.trim().replace(/^"|"$/g, ''))
        .map(header => headerMap[header] || header)
        .filter(header => header);

      const parsedEvents = await Promise.all(rows.slice(1)
        .map(async (row, rowIndex) => {
          // Split by comma but respect quotes, including newlines within quotes
          const values = row.match(/("[\s\S]*?"|[^",]+)(?=\s*,|\s*$)/g)
            ?.map(value => value.replace(/^"|"$/g, '').trim()) || [];

          const event: TempEvent = {};
          let hasError = false;
          const missing: MissingFields = {};

          // Process fields
          for (let i = 0; i < headers.length; i++) {
            const header = headers[i];
            const value = values[i];
            
            // Check for missing or empty values
            if (!value || value.trim() === '') {
              // Add all required fields to check
              if (['title', 'description', 'place', 'startDateTime', 'event_type'].includes(header)) {
                missing[header as keyof MissingFields] = true;
              }
              continue;
            }

            try {
              switch(header) {
                case 'title':
                case 'description':
                case 'place':
                case 'event_type':
                  const trimmedValue = value.replace(/\\n/g, '\n').trim();
                  if (trimmedValue === '') {
                    missing[header as keyof MissingFields] = true;
                  } else {
                    event[header] = trimmedValue;
                  }
                  break;
                case 'startDateTime':
                case 'endDateTime':
                  if (value && value.match(/^\d{2}\/\d{2}\/\d{4}\s\d{2}:\d{2}$/)) {
                    const [datePart, timePart] = value.split(' ');
                    const [day, month, year] = datePart.split('/');
                    const [hours, minutes] = timePart.split(':');
                    
                    const dateObj = new Date(
                      parseInt(year),
                      parseInt(month) - 1,
                      parseInt(day),
                      parseInt(hours),
                      parseInt(minutes)
                    );

                    if (!isNaN(dateObj.getTime())) {
                      event[header] = dateObj.toISOString();
                    } else {
                      throw new Error('Invalid date values');
                    }
                  } else {
                    throw new Error(`Invalid format in value: ${value}`);
                  }
                  break;
                case 'latitude':
                case 'longitude':
                  const num = parseFloat(value);
                  if (!isNaN(num)) {
                    if (header === 'latitude') {
                      event._tempLat = num;
                    } else {
                      event._tempLng = num;
                    }
                  }
                  break;
                case 'imageUrl':
                  if (value) {
                    event._tempImageUrl = value;
                  }
                  break;
              }
            } catch (error) {
              console.error(`Error parsing ${header}:`, error);
              errors.push({
                row: rowIndex + 2,
                message: `Invalid ${header}: ${error.message}`
              });
              hasError = true;
            }
          }

          // After the loop, check for missing required fields
          if (!event.place || event.place.trim() === '') {
            missing.place = true;
            event.place = 'Tel-Aviv'; // Changed from 'Tel Aviv' to 'Tel-Aviv'
          }

          if (!event.event_type || event.event_type === 'Other') {
            missing.event_type = true;
            event.event_type = 'Other'; // Set default and mark as missing
          }

          // Create GeoPoint if both coordinates are valid
          if (typeof event._tempLat === 'number' && typeof event._tempLng === 'number') {
            try {
              event.lat_long = new GeoPoint(event._tempLat, event._tempLng);
            } catch (error) {
              errors.push({
                row: rowIndex + 2,
                message: `Invalid coordinates: ${error.message}`
              });
              hasError = true;
            }
          }

          // Then delete the temp properties
          delete event._tempLat;
          delete event._tempLng;

          // Check for missing coordinates
          if (!event.lat_long) {
            missing.lat_long = true;
          }

          // Set missing fields in the event object
          event._missingFields = missing;

          return hasError ? null : event;
        }));

      setParseErrors(errors);
      setEvents(parsedEvents.filter(event => event !== null));
      setProcessing(true);
    };
    
    reader.readAsText(file);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseCsvFile(file);
    }
  };

  const handleNext = async () => {
    if (currentEvent) {
      try {
        // Only create document if not skipping
        let imageBlob: Blob | null = null;
        if (currentEvent._tempImageUrl) {
          setImageLoadingStatus(prev => ({ 
            ...prev, 
            [currentEvent._tempImageUrl!]: 'Downloading image...' 
          }));
          
          const response = await fetch(currentEvent._tempImageUrl);
          imageBlob = await response.blob();
        }

        // Initialize and create event
        const eventToCreate: Event = {
          ...currentEvent as Event,
          likes: 0,
          comments: 0,
          shares: 0,
          Image_gallery_url: [],
          isPublic: true,
          userId: 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
          user_name: 'niv.beeri@gmail.com',
          event_type: currentEvent.event_type || 'Other',
          startDateTime: typeof currentEvent.startDateTime === 'string' 
            ? Timestamp.fromDate(new Date(currentEvent.startDateTime))
            : currentEvent.startDateTime || Timestamp.now(),
          endDateTime: typeof currentEvent.endDateTime === 'string'
            ? Timestamp.fromDate(new Date(currentEvent.endDateTime))
            : currentEvent.endDateTime || null
        };

        await eventService.createEvent(
          eventToCreate,
          imageBlob ? [new File([imageBlob], `image_${Date.now()}.jpg`, { type: 'image/jpeg' })] : []
        );

        // Move to next event after successful creation
        if (currentIndex < events.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          onClose();
        }
      } catch (error) {
        console.error('Error processing event:', error);
      }
    }
  };

  const handleSkip = () => {
    if (currentIndex < events.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => prev - 1);
  };

  if (!processing) {
    return (
      <div className="csv-import-container">
        <h2>Import Events from CSV</h2>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="file-input"
        />
      </div>
    );
  }

  return (
    <div className="csv-import-container">
      {!processing ? (
        <h2>This should never show</h2>
      ) : (
        <>
          <div className="import-header">
            <h2>Review Events</h2>
            <div className="progress-info">
              Event {currentIndex + 1} of {events.length}
            </div>
          </div>

          {parseErrors.length > 0 && (
            <div className="parse-errors">
              <h3>Warnings:</h3>
              <ul>
                {parseErrors.map((error, index) => (
                  <li key={index}>
                    Row {error.row}: {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {currentEvent && (
            <>
              <EventForm
                key={currentIndex}
                event={{
                  ...currentEvent as Event,
                  imageUrl: currentEvent._tempImageUrl
                }}
                onSubmit={handleNext}
                missingFields={currentEvent._missingFields}
              />
              {Object.entries(imageLoadingStatus).map(([url, status]) => (
                <div key={url} className="image-status">
                  <small>{status}</small>
                </div>
              ))}
            </>
          )}

          <div className="import-actions">
            <button 
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="button secondary"
            >
              Previous
            </button>
            <button 
              onClick={handleSkip} 
              className="button secondary"
            >
              Skip
            </button>
            <button 
              onClick={handleNext} 
              className="button primary"
            >
              {currentIndex === events.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CsvImport; 